import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowLeft,
  Dumbbell,
  Quote,
  Plus,
  Trash2,
  Edit,
  Loader2,
  Save
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_3e64acab-2561-42e6-b860-06b1a563df4d/artifacts/5nvw3yv2_6ECA6BBA-F429-4238-A743-EE6BF2D1D33F.png";

export default function AdminContent() {
  const navigate = useNavigate();
  const [exercises, setExercises] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [newQuote, setNewQuote] = useState({ text: "", category: "general" });
  const [newExercise, setNewExercise] = useState({
    name: "",
    description: "",
    muscle_groups: "",
    gif_url: "",
    duration_seconds: "",
    reps: "",
    sets: 3,
    difficulty: "beginner"
  });

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin");
      return;
    }
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const [exercisesRes, quotesRes] = await Promise.all([
        axios.get(`${API}/admin/exercises`),
        axios.get(`${API}/admin/quotes`)
      ]);
      setExercises(exercisesRes.data.exercises);
      setQuotes(quotesRes.data.quotes);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate("/admin");
      }
      toast.error("Failed to load content");
    } finally {
      setLoading(false);
    }
  };

  const handleAddQuote = async () => {
    if (!newQuote.text) {
      toast.error("Please enter quote text");
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/admin/quotes`, newQuote);
      toast.success("Quote added!");
      setNewQuote({ text: "", category: "general" });
      fetchContent();
    } catch (error) {
      toast.error("Failed to add quote");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteQuote = async (quoteId) => {
    try {
      await axios.delete(`${API}/admin/quotes/${quoteId}`);
      toast.success("Quote deleted");
      fetchContent();
    } catch (error) {
      toast.error("Failed to delete quote");
    }
  };

  const handleAddExercise = async () => {
    if (!newExercise.name || !newExercise.description) {
      toast.error("Please fill in required fields");
      return;
    }
    
    setSaving(true);
    try {
      const exerciseData = {
        ...newExercise,
        muscle_groups: newExercise.muscle_groups.split(",").map(m => m.trim()),
        duration_seconds: newExercise.duration_seconds ? parseInt(newExercise.duration_seconds) : null,
        reps: newExercise.reps ? parseInt(newExercise.reps) : null,
        sets: parseInt(newExercise.sets)
      };
      
      await axios.post(`${API}/admin/exercises`, exerciseData);
      toast.success("Exercise added!");
      setNewExercise({
        name: "",
        description: "",
        muscle_groups: "",
        gif_url: "",
        duration_seconds: "",
        reps: "",
        sets: 3,
        difficulty: "beginner"
      });
      fetchContent();
    } catch (error) {
      toast.error("Failed to add exercise");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteExercise = async (exerciseId) => {
    try {
      await axios.delete(`${API}/admin/exercises/${exerciseId}`);
      toast.success("Exercise deleted");
      fetchContent();
    } catch (error) {
      toast.error("Failed to delete exercise");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary animate-pulse text-2xl font-bold">LOADING...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/admin/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-5 w-5" /> Dashboard
            </Link>
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Coach Coop" className="h-8 w-8 rounded-sm" />
              <span className="font-bold tracking-tight">CONTENT MANAGER</span>
            </div>
            <div className="w-24" />
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <Tabs defaultValue="quotes" className="w-full">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-8">
            <TabsTrigger value="quotes" className="uppercase font-bold tracking-wider">
              Quotes
            </TabsTrigger>
            <TabsTrigger value="exercises" className="uppercase font-bold tracking-wider">
              Exercises
            </TabsTrigger>
          </TabsList>

          {/* Quotes Tab */}
          <TabsContent value="quotes">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Add Quote Form */}
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="uppercase tracking-tighter flex items-center gap-2">
                    <Plus className="h-5 w-5 text-primary" />
                    Add Quote
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label className="uppercase text-sm tracking-wider">Quote Text</Label>
                    <Textarea
                      value={newQuote.text}
                      onChange={(e) => setNewQuote(prev => ({ ...prev, text: e.target.value }))}
                      placeholder="Enter motivational quote..."
                      className="bg-input border-transparent focus:border-primary rounded-sm min-h-[100px]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="uppercase text-sm tracking-wider">Category</Label>
                    <Input
                      value={newQuote.category}
                      onChange={(e) => setNewQuote(prev => ({ ...prev, category: e.target.value }))}
                      placeholder="general"
                      className="bg-input border-transparent focus:border-primary rounded-sm"
                    />
                  </div>
                  <Button
                    onClick={handleAddQuote}
                    disabled={saving}
                    className="w-full bg-primary hover:bg-primary/90 uppercase font-bold tracking-widest rounded-sm"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add Quote"}
                  </Button>
                </CardContent>
              </Card>

              {/* Quotes List */}
              <Card className="bg-card border-border lg:col-span-2">
                <CardHeader>
                  <CardTitle className="uppercase tracking-tighter flex items-center gap-2">
                    <Quote className="h-5 w-5 text-primary" />
                    All Quotes ({quotes.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {quotes.map((quote, i) => (
                      <motion.div
                        key={quote.id || i}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="flex items-start justify-between gap-4 p-3 bg-secondary/50 rounded-sm"
                      >
                        <div className="flex-1">
                          <p className="italic">"{quote.text}"</p>
                          <span className="text-xs text-muted-foreground uppercase">{quote.category || 'general'}</span>
                        </div>
                        {quote.id && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteQuote(quote.id)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </motion.div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Exercises Tab */}
          <TabsContent value="exercises">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Add Exercise Form */}
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="uppercase tracking-tighter flex items-center gap-2">
                    <Plus className="h-5 w-5 text-primary" />
                    Add Exercise
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label className="uppercase text-sm tracking-wider">Name *</Label>
                    <Input
                      value={newExercise.name}
                      onChange={(e) => setNewExercise(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Push-Ups"
                      className="bg-input border-transparent focus:border-primary rounded-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="uppercase text-sm tracking-wider">Description *</Label>
                    <Textarea
                      value={newExercise.description}
                      onChange={(e) => setNewExercise(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Classic chest and tricep builder"
                      className="bg-input border-transparent focus:border-primary rounded-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="uppercase text-sm tracking-wider">Muscle Groups (comma separated)</Label>
                    <Input
                      value={newExercise.muscle_groups}
                      onChange={(e) => setNewExercise(prev => ({ ...prev, muscle_groups: e.target.value }))}
                      placeholder="chest, triceps, shoulders"
                      className="bg-input border-transparent focus:border-primary rounded-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="uppercase text-sm tracking-wider">GIF URL</Label>
                    <Input
                      value={newExercise.gif_url}
                      onChange={(e) => setNewExercise(prev => ({ ...prev, gif_url: e.target.value }))}
                      placeholder="https://..."
                      className="bg-input border-transparent focus:border-primary rounded-sm"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="uppercase text-sm tracking-wider">Duration (sec)</Label>
                      <Input
                        type="number"
                        value={newExercise.duration_seconds}
                        onChange={(e) => setNewExercise(prev => ({ ...prev, duration_seconds: e.target.value }))}
                        placeholder="60"
                        className="bg-input border-transparent focus:border-primary rounded-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="uppercase text-sm tracking-wider">Reps</Label>
                      <Input
                        type="number"
                        value={newExercise.reps}
                        onChange={(e) => setNewExercise(prev => ({ ...prev, reps: e.target.value }))}
                        placeholder="15"
                        className="bg-input border-transparent focus:border-primary rounded-sm"
                      />
                    </div>
                  </div>
                  <Button
                    onClick={handleAddExercise}
                    disabled={saving}
                    className="w-full bg-primary hover:bg-primary/90 uppercase font-bold tracking-widest rounded-sm"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add Exercise"}
                  </Button>
                </CardContent>
              </Card>

              {/* Exercises List */}
              <Card className="bg-card border-border lg:col-span-2">
                <CardHeader>
                  <CardTitle className="uppercase tracking-tighter flex items-center gap-2">
                    <Dumbbell className="h-5 w-5 text-primary" />
                    Exercise Library ({exercises.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 gap-3 max-h-[500px] overflow-y-auto">
                    {exercises.map((exercise, i) => (
                      <motion.div
                        key={exercise.id || i}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="p-3 bg-secondary/50 rounded-sm"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="font-bold uppercase tracking-tight">{exercise.name}</div>
                            <div className="text-sm text-muted-foreground mb-2">{exercise.description}</div>
                            <div className="flex flex-wrap gap-1">
                              {exercise.muscle_groups?.map((muscle, j) => (
                                <span key={j} className="px-2 py-0.5 bg-accent/20 text-accent text-xs uppercase rounded-sm">
                                  {muscle}
                                </span>
                              ))}
                            </div>
                          </div>
                          {exercise.id && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteExercise(exercise.id)}
                              className="text-destructive hover:text-destructive flex-shrink-0"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
